import Keycloak, {
	KeycloakConfig,
	KeycloakTokenParsed,
} from "keycloak-js"
import { Config } from "Config"
import { NetworkService } from "@sinossi/mates-react-library"
import { StorageEntries } from "services/storage-entries"

export type AuthenticationPromiseResolve = () => void
export type AuthenticationPromiseReject = (reason?: any) => void

export class AuthenticationService
{
	private static INSTANCE: AuthenticationService|null = null

	private readonly envConfig: Config = Config.init()

	private _keycloak?: Keycloak = undefined

	private constructor()
	{
	}

	public static init(): AuthenticationService
	{
		if(AuthenticationService.INSTANCE === null) {
			AuthenticationService.INSTANCE = new AuthenticationService()
		}

		return AuthenticationService.INSTANCE
	}

	public setupAuthentication = (): Promise<void> => {
		return new Promise((resolve: AuthenticationPromiseResolve, reject: AuthenticationPromiseReject): void => {
			this.setupKeycloak(resolve, reject)
		})
	}

	private setupKeycloak = (
		resolve: AuthenticationPromiseResolve,
		reject: AuthenticationPromiseReject
	): void => {
		if(!this.envConfig.keycloakEnabled) {
			console.info("[storebook] Keycloak disabled. Skipping initialisation")
			// this.setupProfile()
			resolve()
			return
		}

		const config: KeycloakConfig = {
			url: this.envConfig.keycloakUrl,
			realm: this.envConfig.keycloakRealm,
			clientId: this.envConfig.keycloakClientId,
		} as KeycloakConfig
		if(!this.validateKeycloakConfig(config)) {
			const error: string = "[storebook] Keycloak config is not valid: check previous error for details. Skipping initialisation"
			console.error(error)
			reject(error)
			return
		}
		this.initKeycloak(config, resolve, reject)
	}

	private validateKeycloakConfig = (config: any): boolean => {
		if(config.url ===  undefined || config.url === null) {
			console.error("[storebook] validateKeycloakConfig - url null or undefined")
			return false
		}
		if(config.realm ===  undefined || config.realm === null) {
			console.error("[storebook] validateKeycloakConfig - realm null or undefined")
			return false
		}
		if(config.clientId ===  undefined || config.clientId === null) {
			console.error("[storebook] validateKeycloakConfig - realm null or undefined")
			return false
		}

		return true
	}

	private initKeycloak(
		config: KeycloakConfig,
		resolve: AuthenticationPromiseResolve,
		reject: AuthenticationPromiseReject
	): void
	{
		this._keycloak = new Keycloak(config)
		this._keycloak!.init({onLoad: "login-required", checkLoginIframe: false})
			.then((authenticated: boolean) => this.onLoginRequired(authenticated, resolve, reject))
			.catch(reason => {
				console.error("[storebook] Cannot init keycloak")
				console.error(reason)
				NetworkService.clearAuthenticationTokens()

				// let { navigate } = this.props
				// navigate("/keycloak-error")
				reject(reason)
			})
		this._keycloak.onTokenExpired = () => {
			this.onTokenExpired()
		}
	}

	private onLoginRequired = (
		authenticated: boolean,
		resolve: AuthenticationPromiseResolve,
		reject: AuthenticationPromiseReject
	): void => {
		if(this._keycloak!.token === undefined) {
			const error: string = "[storebook] Received Keycloak token is undefined. Ignoring."
			console.info(error)
			reject(error)
			return
		}

		console.info("[storebook] successfully authenticated? " + authenticated)
		console.debug(`[storebook] received token: ${this._keycloak!.token}`)
		NetworkService.saveAuthenticationToken(this._keycloak!.token!)

		// this.setupProfile()
		resolve()
	}

	private onTokenExpired = (): void => {
		console.info("[storebook] token expired")
		this._keycloak!.updateToken(this.envConfig.keycloakTokenValidity)
			.then(() => {
				if(this._keycloak!.token === undefined) {
					console.info("[storebook] Received Keycloak token is undefined. Not updating it.")
					return
				}

				console.info("[storebook] successfully get a new token")
				console.debug(`[storebook] received token: ${this._keycloak!.token}`)
				NetworkService.saveAuthenticationToken(this._keycloak!.token!)
			})
			.catch(() => {
				console.error("error refreshing token.")
			})
	}

	public logout = (): void => {
		StorageEntries.clearSession()
		this.keycloak?.logout()
	}

	public isAuthenticated = (): boolean => {
		return this.keycloak?.authenticated === true
	}

	public userHasRole = (role: string): boolean => {
		const realm =  this.keycloak?.hasRealmRole(role) ?? false
		const resource = this.keycloak?.hasResourceRole(role) ?? false
		console.log(`Realm=${realm} - Resource=${resource}`)
		return realm || resource
	}

	public getUserInfos = (): KeycloakTokenParsed|undefined => {
		return this.keycloak?.idTokenParsed
	}

	public get keycloak(): Keycloak|undefined
	{
		return this._keycloak
	}
}
